import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import {
  CustomDomainPlatformType,
  CustomDomainsApi,
  CustomDomainTarget,
  CustomDomainType,
} from '@va/shared/api/custom-domains';
import { useGetTargetedDNSRecords } from '@va/shared/api/custom-domains/custom-domains.api.hooks';
import { selectAgencyId } from '@va/standalone/shared/selectors';
import { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAgencyCustomDashboardDomains } from '../../shared/useAgencyCustomDomains';

type CustomDashboardDomainCreationContext = ReturnType<typeof useContextValue>;

const CustomDashboardDomainCreationContext = createContext<CustomDashboardDomainCreationContext>(
  {} as CustomDashboardDomainCreationContext,
);

export enum CustomDashboardDomainCreationStep {
  verifyDNS = 'verifyDNS',
  confirmEffects = 'confirmEffects',
}

export type CustomDashboardDomainCreationContextParams = {
  closeModal: () => void;
  domain: string;
  customDomainId: number | undefined; // if this is provided, we perform activation instead of creation
  onSuccess?: () => void;
};

export const CustomDashboardDomainCreationContextProvider = ({
  children,
  ...rest
}: PropsWithChildren<CustomDashboardDomainCreationContextParams>) => {
  const value = useContextValue({ ...rest });
  return (
    <CustomDashboardDomainCreationContext.Provider value={value}>
      {children}
    </CustomDashboardDomainCreationContext.Provider>
  );
};

const useContextValue = ({
  domain,
  closeModal,
  onSuccess,
  customDomainId,
}: CustomDashboardDomainCreationContextParams) => {
  const [activeStep, setActiveStep] = useState<CustomDashboardDomainCreationStep>(
    CustomDashboardDomainCreationStep.verifyDNS,
  );

  const translate = useTranslate();

  const dnsRecordsReq = useGetTargetedDNSRecords(
    !domain
      ? null
      : {
          platform: CustomDomainPlatformType.AWL,
          domain: domain,
          target: CustomDomainTarget.dashboard,
        },
  );

  const agencyId = useSelector(selectAgencyId);
  const listCustomDomainReq = useAgencyCustomDashboardDomains();

  const { showErrorNotification } = useAddNotification();

  const saveCustomDomain = useCallback(async () => {
    if (!agencyId) return;
    try {
      if (customDomainId) {
        await CustomDomainsApi.activateCompanyDomain({
          companyId: agencyId,
          customDomainId: customDomainId,
        });
      } else {
        await CustomDomainsApi.createCompanyDomain({
          companyId: agencyId,
          domain: domain,
          platform: CustomDomainPlatformType.AWL,
          type: CustomDomainType.dashboard,
        });
      }

      await listCustomDomainReq.mutate();
      closeModal();
      onSuccess?.();
    } catch (error) {
      console.error(error);
      showErrorNotification(undefined, CustomDomainsApi.extractApiErrorMessage(error, translate));
    }
  }, [agencyId, closeModal, customDomainId, domain, listCustomDomainReq, onSuccess, showErrorNotification, translate]);

  return { activeStep, setActiveStep, dnsRecordsReq, closeModal, domain, saveCustomDomain };
};

export const useCustomDashboardDomainCreationContext = () => useContext(CustomDashboardDomainCreationContext);
